import { TaskV1TaskActionsServiceReassign, TaskV1TaskActionsServiceApprove, TaskV1TaskActionsServiceMarkProvisionComplete, TaskV1TaskActionsServiceMarkProvisionErrored, TaskV1TaskActionsServiceRestart, TaskV1TaskActionsServiceDeny, TaskV1TaskActionsServiceComment, TaskV1TaskServiceDelete, TaskV1TaskActionsServiceClose, TaskV1TaskServiceSendReminder } from 'c1gen/permissions'
import { C1MoreButtonOptionProps } from 'components/core/button'
import { SendReminder } from 'components/core/task/send_reminder/index'
import { featureChecker } from 'components/no-ui/capability'
import { OpenPropType } from 'components/no-ui/context/modal-provider'
import { Decision } from 'components/pages/task/action'
import { OnActionFunc } from 'components/pages/task/action/button'
import { canActOn } from 'components/pages/task/state'
import { deleteTask, TasksActions, TaskValue } from 'lib/actions/task'
import { NextRouter } from 'next/router'
import { TaskActionType } from 'pbts/c1/api/task/v1/task'
import { TaskServiceClient } from 'pbts/c1/api/task/v1/task.client'
import { Dispatch, SetStateAction } from 'react'
import { IntlShape } from 'react-intl'

export type OpenDialogType =
    | 'approve'
    | 'deny'
    | 'comment'
    | 'reassign'
    | 'restart'
    | 'bulkReassign'
    | 'bulkApprove'
    | 'bulkSelectedCancel'
    | 'bulkSelectedComment'
    | 'bulkSelectedRestart'
    | 'bulkSelectedReassign'
    | undefined

export type TaskOptionsArgs = {
    intl: IntlShape
    router: NextRouter
    task: TaskValue
    onAction: OnActionFunc
    taskSvc?: TaskServiceClient
    dispatch?: Dispatch<TasksActions>
    hasFeature: featureChecker
    setOpenDialog: (od: OpenDialogType) => void
    setTaskActionTask: Dispatch<SetStateAction<TaskValue>>
}

export type TaskReviewDetailsOptionsArgs = {
    intl: IntlShape
    task: TaskValue
    onAction: OnActionFunc
    hasFeature: featureChecker
    setTaskActionTask: Dispatch<SetStateAction<TaskValue>>
    setOpenDialog: (od: OpenDialogType) => void
    canSendReminder?: boolean
    open?: OpenPropType
}

export const urlFromTask = (task?: TaskValue): string => `/task/${task?.id}`

export const reassignOption = ({ intl, task, setOpenDialog, setTaskActionTask }: TaskOptionsArgs): C1MoreButtonOptionProps => {
    const taskId = task.id

    return {
        permission: TaskV1TaskActionsServiceReassign,
        key: `components_task_reassign_option_${taskId}`,
        label: intl.formatMessage({ id: 'components_task_reassign_option_label', defaultMessage: 'Reassign to...' }),
        onClick: (): void => {
            setTaskActionTask(task)
            setOpenDialog('reassign')
        },
        disabled: !canActOn(task, TaskActionType.REASSIGN),
    }
}

export const approveOption = ({ intl, task, onAction }: TaskOptionsArgs): C1MoreButtonOptionProps => {
    const taskId = task.id

    return {
        permission: TaskV1TaskActionsServiceApprove,
        key: `components_task_list_approve_option_${taskId}`,
        label: intl.formatMessage({ id: 'component_task_list_approve_option_label', defaultMessage: 'Approve' }),
        onClick: (): void => {
            onAction(
                {
                    decision: Decision.Approve,
                    comment: '',
                    policyStepId: task.policy?.current?.id,
                },
                task,
            )
        },
        disabled: !canActOn(task, TaskActionType.APPROVE),
    }
}

export const markProvisionComplete = ({ intl, task, onAction }: TaskOptionsArgs): C1MoreButtonOptionProps => {
    const taskId = task.id

    return {
        permission: TaskV1TaskActionsServiceMarkProvisionComplete,
        key: `components_task_list_provision_complete_option_${taskId}`,
        label:
            task.type?.taskType.oneofKind === 'grant'
                ? intl.formatMessage({
                    id: 'component_task_list_provision_complete_option_label',
                    defaultMessage: 'Provisioning complete',
                })
                : intl.formatMessage({
                    id: 'component_task_list_deprovision_complete_option_label',
                    defaultMessage: 'Deprovisioning complete',
                }),
        onClick: (): void => {
            onAction(
                {
                    decision: Decision.ProvisionComplete,
                    comment: '',
                    policyStepId: task.policy?.current?.id,
                },
                task,
            )
        },
        disabled: !canActOn(task, TaskActionType.PROVISION_COMPLETE),
    }
}

export const markProvisionErrored = ({ intl, task, onAction }: TaskOptionsArgs): C1MoreButtonOptionProps => {
    const taskId = task.id

    return {
        permission: TaskV1TaskActionsServiceMarkProvisionErrored,
        key: `components_task_list_provision_errored_option_${taskId}`,
        label:
            task.type?.taskType.oneofKind === 'grant'
                ? intl.formatMessage({
                    id: 'component_task_list_provision_complete_option_label',
                    defaultMessage: 'Won\'t provision',
                })
                : intl.formatMessage({
                    id: 'component_task_list_deprovision_complete_option_label',
                    defaultMessage: 'Won\'t deprovision',
                }),
        onClick: (): void => {
            onAction(
                {
                    decision: Decision.ProvisionErrored,
                    comment: '',
                    policyStepId: task.policy?.current?.id,
                },
                task,
            )
        },
        disabled: !canActOn(task, TaskActionType.PROVISION_ERRORED),
    }
}

export const restartOption = ({
    intl,
    task,
    setTaskActionTask,
    setOpenDialog,
}: TaskReviewDetailsOptionsArgs): C1MoreButtonOptionProps => {
    const taskId = task.id

    return {
        permission: TaskV1TaskActionsServiceRestart,
        key: `components_task_list_restart_option_${taskId}`,
        label: intl.formatMessage({ id: 'component_task_list_restart_option_label', defaultMessage: 'Restart' }),
        onClick: (): void => {
            setTaskActionTask(task)
            setOpenDialog('restart')
        },
        disabled: !canActOn(task, TaskActionType.RESTART),
    }
}

export const denyOption = ({ intl, task, onAction }: TaskOptionsArgs): C1MoreButtonOptionProps => {
    const taskId = task.id

    return {
        permission: TaskV1TaskActionsServiceDeny,
        key: `components_task_list_deny_option_${taskId}`,
        label: intl.formatMessage({ id: 'component_task_list_deny_option_label', defaultMessage: 'Deny' }),
        onClick: (): void => {
            onAction(
                {
                    decision: Decision.Deny,
                    comment: '',
                    policyStepId: task.policy?.current?.id,
                },
                task,
            )
        },
        disabled: !canActOn(task, TaskActionType.DENY),
    }
}

export const commentOption = ({ intl, task, setOpenDialog, setTaskActionTask }: TaskOptionsArgs): C1MoreButtonOptionProps => {
    const taskId = task.id

    return {
        permission: TaskV1TaskActionsServiceComment,
        key: `components_task_list_comment_option_${taskId}`,
        label: intl.formatMessage({ id: 'component_task_list_comment_option_label', defaultMessage: 'Comment' }),
        onClick: (): void => {
            setTaskActionTask(task)
            setOpenDialog('comment')
        },
        disabled: !canActOn(task, TaskActionType.COMMENT),
    }
}

export const approveWithCommentOption = ({
    intl,
    task,
    setOpenDialog,
    setTaskActionTask,
}: TaskOptionsArgs): C1MoreButtonOptionProps => {
    const taskId = task.id

    const getLabel = (taskValue: TaskValue): string => {
        switch (taskValue.type?.taskType.oneofKind) {
            case 'certify':
                return intl.formatMessage({
                    id: 'component_task_list_certify_comment_option_label',
                    defaultMessage: 'Certify with comment',
                })
            case 'revoke':
                return intl.formatMessage({
                    id: 'component_task_list_confirm_comment_option_label',
                    defaultMessage: 'Confirm with comment',
                })
            case 'grant':
            default:
                // This is more generic
                return intl.formatMessage({
                    id: 'component_task_list_approve_comment_option_label',
                    defaultMessage: 'Approve with comment',
                })
        }
    }

    return {
        permission: TaskV1TaskActionsServiceApprove,
        key: `components_task_list_certify_comment_option_${taskId}`,
        label: getLabel(task),
        onClick: (): void => {
            setTaskActionTask(task)
            setOpenDialog('approve')
        },
        disabled: !canActOn(task, TaskActionType.APPROVE),
    }
}

export const denyWithCommentOption = ({
    intl,
    task,
    setOpenDialog,
    setTaskActionTask,
}: TaskOptionsArgs): C1MoreButtonOptionProps => {
    const taskId = task.id

    const getLabel = (taskValue: TaskValue): string => {
        switch (taskValue.type?.taskType.oneofKind) {
            case 'revoke':
                return intl.formatMessage({
                    id: 'component_task_list_cancel_comment_option_label',
                    defaultMessage: 'Decline with comment',
                })
            case 'grant':
            case 'certify':
            default:
                // This is more generic
                return intl.formatMessage({
                    id: 'component_task_list_deny_comment_option_label',
                    defaultMessage: 'Deny with comment',
                })
        }
    }

    return {
        permission: TaskV1TaskActionsServiceDeny,
        key: `components_task_list_deny_comment_option_${taskId}`,
        label: getLabel(task),
        onClick: (): void => {
            setTaskActionTask(task)
            setOpenDialog('deny')
        },
        disabled: !canActOn(task, TaskActionType.DENY),
    }
}

export const deleteOption = ({ intl, task, taskSvc, dispatch }: TaskOptionsArgs): C1MoreButtonOptionProps => {
    const handleDelete = (): void => {
        if (!dispatch) {
            return
        }
        deleteTask(dispatch, taskSvc, task.id)
    }

    return {
        permission: TaskV1TaskServiceDelete,
        key: 'components_task_details_delete_option',
        label: intl.formatMessage({
            id: 'components_task_details_delete_option_label',
            defaultMessage: 'Delete',
        }),
        onClick: handleDelete,
        disabled: !canActOn(task, TaskActionType.DELETE),
    }
}

export const cancelOption = ({ intl, task, onAction }: TaskReviewDetailsOptionsArgs): C1MoreButtonOptionProps => {
    const taskId = task.id

    return {
        permission: TaskV1TaskActionsServiceClose,
        key: `components_task_list_cancel_option_${taskId}`,
        label: intl.formatMessage({ id: 'component_task_list_cancel_option_label', defaultMessage: 'Cancel' }),
        onClick: (): void => {
            onAction(
                {
                    decision: Decision.Cancel,
                    comment: '',
                },
                task,
            )
        },
        disabled: !canActOn(task, TaskActionType.CLOSE),
    }
}

export const sendReminderOption = ({ intl, task, open }: TaskReviewDetailsOptionsArgs): C1MoreButtonOptionProps => {
    return {
        permission: TaskV1TaskServiceSendReminder,
        key: `components_task_list_send_reminder_option_${task.id}`,
        label: intl.formatMessage({
            id: 'component_send_reminder_option_label',
            defaultMessage: 'Send reminder',
        }),
        onClick: (): void => {
            open && open(SendReminder, { taskID: task.id })()
        },
        disabled: !canActOn(task, TaskActionType.SEND_REMINDER),
    }
}
